import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Card,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Grid,
  GridItem,
  Icon,
  Badge,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  Container,
  Divider,
  SimpleGrid,
  useToast,
  Tooltip,
} from '@chakra-ui/react';
import {
  FiUsers,
  FiMusic,
  FiDollarSign,
  FiPlus,
  FiSettings,
  FiBarChart2
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../services/axios';
import ArtistSwitcher from './ArtistSwitcher';
import { useAuth } from '../../../hooks/useAuth';

const MerchantDashboard = () => {
  const [merchantData, setMerchantData] = useState(null);
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const toast = useToast();
  const { isSwitchedContext } = useAuth();


  useEffect(() => {
    fetchMerchantData();
    fetchArtists();
  }, []);

  // Block access to merchant dashboard when switched to artist context
  useEffect(() => {
    if (isSwitchedContext) {
      navigate('/dashboard/artist/tracks', { replace: true });
      toast({
        title: 'Access Restricted',
        description: 'You are currently viewing as an artist. Use "Switch back to Merchant" to access merchant dashboard.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
    }
  }, [isSwitchedContext, navigate, toast]);

  const fetchMerchantData = async () => {
    try {
      const response = await axiosInstance.get('/merchants/profile');
      setMerchantData(response.data);
    } catch (error) {
      console.error('Error fetching merchant data:', error);
      setError('Failed to load merchant profile');
    }
  };

  const fetchArtists = async () => {
    try {
      const response = await axiosInstance.get('/merchants/artists');
      setArtists(response.data);
    } catch (error) {
      console.error('Error fetching artists:', error);
      setError('Failed to load artists');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateArtist = () => {
    navigate('/merchant/artists/create');
  };

  const handleManageArtists = () => {
    navigate('/merchant/artists');
  };

  const handleSettings = () => {
    navigate('/merchant/settings');
  };

  if (loading) {
    return (
      <Container maxW="7xl" centerContent py={20}>
        <VStack spacing={4}>
          <Spinner size="xl" color="dark.900" thickness="3px" />
          <Text color="primary.900" fontSize="lg">Loading your merchant dashboard...</Text>
        </VStack>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxW="7xl" py={8}>
        <Alert status="error" bg="red.50" borderColor="red.200" color="red.800">
          <AlertIcon color="red.500" />
          {error}
        </Alert>
      </Container>
    );
  }

  const stats = [
    {
      label: 'Managed Artists',
      value: artists.length,
      icon: FiUsers,
      color: 'dark.900',
      helpText: 'Total artists under management'
    },
    {
      label: 'Active Tracks',
      value: artists.reduce((total, artist) => total + (artist.track_count || 0), 0),
      icon: FiMusic,
      color: 'dark.900',
      helpText: 'Published across all artists'
    },
    {
      label: 'Total Earnings',
      value: `$${artists.reduce((total, artist) => total + (artist.earnings || 0), 0).toFixed(2)}`,
      icon: FiDollarSign,
      color: 'dark.900',
      helpText: 'Combined earnings from all artists'
    },
    {
      label: 'Verification Status',
      value: merchantData?.verification_status || 'Pending',
      icon: FiBarChart2,
      color: 'dark.900',
      helpText: 'Account verification status'
    }
  ];

  return (
    <Container maxW="7xl" py={{ base: 4, md: 8 }} px={{ base: 4, md: 6 }}>
      <VStack spacing={{ base: 6, md: 8 }} align="stretch">
        {/* Header */}
        <Box>
          <Flex
            align={{ base: "start", md: "center" }}
            direction={{ base: "column", md: "row" }}
            gap={4}
          >
            <VStack align="start" spacing={1} flex={1}>
              <Heading size="xl" color="dark.900" fontFamily="heading">
                {merchantData?.company_name}
              </Heading>
              <Text color="primary.900" fontSize="lg">
                Welcome back! Here's your merchant overview.
              </Text>
            </VStack>
            <HStack spacing={{ base: 2, md: 3 }} flexShrink={0} flexWrap={{ base: "wrap", md: "nowrap" }}>
              <ArtistSwitcher artists={artists} />
              <Button
                leftIcon={<Icon as={FiSettings} />}
                variant="ghost"
                color="primary.900"
                _hover={{ bg: "dark.900", color: "primary.100" }}
                onClick={handleSettings}
              >
                Settings
              </Button>
            </HStack>
          </Flex>
          <Divider borderColor="secondary.900" mt={6} />
        </Box>

        {/* Stats Grid */}
        <SimpleGrid columns={{ base: 1, sm: 2, xl: 4 }} spacing={{ base: 4, md: 6 }}>
          {stats.map((stat, index) => {
            // Make "Managed Artists" card clickable
            const isManagedArtists = index === 0;
            const cardProps = isManagedArtists ? {
              onClick: handleManageArtists,
              cursor: "pointer",
              _hover: {
                transform: "translateY(-4px)",
                shadow: "xl",
                borderColor: "dark.900"
              }
            } : {
              _hover: {
                transform: "translateY(-2px)",
                shadow: "xl"
              }
            };

            return (
              <Tooltip
                key={index}
                label={isManagedArtists ? "Click to manage artists" : ""}
                hasArrow
                placement="top"
                isDisabled={!isManagedArtists}
              >
                <Card
                  bg="dark.500"
                  borderColor="secondary.900"
                  borderWidth="1px"
                  shadow="lg"
                  transition="all 0.2s"
                  {...cardProps}
                >
                  <CardBody p={{ base: 4, md: 6 }}>
                    <Stat>
                      <Flex align="center" mb={3}>
                        <Box
                          p={2}
                          rounded="lg"
                          bg="secondary.900"
                          display="inline-flex"
                        >
                          <Icon as={stat.icon} color={stat.color} boxSize={5} />
                        </Box>
                        <StatLabel ml={3} fontSize="sm" fontWeight="medium" color="primary.900">
                          {stat.label}
                        </StatLabel>
                      </Flex>
                      <StatNumber fontSize="3xl" color="dark.900" fontWeight="bold">
                        {stat.value}
                      </StatNumber>
                      <StatHelpText fontSize="xs" color="primary.900" mt={2}>
                        {stat.helpText}
                      </StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>
              </Tooltip>
            );
          })}
        </SimpleGrid>

        {/* Quick Actions & Account Status */}
        <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={{ base: 4, md: 6 }}>
          <GridItem>
            <Card bg="dark.500" borderColor="secondary.900" borderWidth="1px" shadow="lg">
              <CardBody p={{ base: 4, md: 6 }}>
                <VStack align="start" spacing={{ base: 4, md: 6 }}>
                  <Heading size="lg" color="dark.900" fontFamily="heading">
                    Quick Actions
                  </Heading>
                  <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={{ base: 3, md: 4 }} w="full">
                    <Tooltip
                      label="Your merchant account must be verified before you can create linked artists"
                      isDisabled={merchantData?.verification_status === 'verified'}
                      hasArrow
                      placement="top"
                    >
                      <Button
                        leftIcon={<Icon as={FiPlus} />}
                        variant="brand"
                        size="lg"
                        onClick={handleCreateArtist}
                        isDisabled={merchantData?.verification_status !== 'verified'}
                        w="full"
                        _hover={{
                          transform: merchantData?.verification_status === 'verified' ? "translateY(-1px)" : "none",
                          boxShadow: merchantData?.verification_status === 'verified' ? "lg" : "none"
                        }}
                        transition="all 0.2s"
                        opacity={merchantData?.verification_status !== 'verified' ? 0.6 : 1}
                      >
                        Add New Artist
                      </Button>
                    </Tooltip>
                    <Button
                      leftIcon={<Icon as={FiUsers} />}
                      variant="ghost"
                      size="lg"
                      onClick={handleManageArtists}
                      w="full"
                      color="primary.900"
                      border="1px solid"
                      borderColor="secondary.900"
                      _hover={{
                        bg: "secondary.900",
                        color: "primary.100",
                        borderColor: "dark.900"
                      }}
                      transition="all 0.2s"
                    >
                      Manage Artists
                    </Button>
                  </SimpleGrid>
                </VStack>
              </CardBody>
            </Card>
          </GridItem>

          <GridItem>
            <Card bg="dark.500" borderColor="secondary.900" borderWidth="1px" shadow="lg">
              <CardBody p={{ base: 4, md: 6 }}>
                <VStack align="start" spacing={{ base: 4, md: 6 }}>
                  <Heading size="lg" color="dark.900" fontFamily="heading">
                    Account Status
                  </Heading>
                  <VStack align="start" spacing={{ base: 3, md: 4 }} w="full">
                    <Flex justify="space-between" w="full" align="center">
                      <Text fontSize="sm" fontWeight="medium" color="primary.900">Verification:</Text>
                      <Badge
                        colorScheme={merchantData?.verification_status === 'verified' ? 'green' : 'orange'}
                        textTransform="capitalize"
                        px={3}
                        py={1}
                        borderRadius="full"
                      >
                        {merchantData?.verification_status || 'Pending'}
                      </Badge>
                    </Flex>
                    <Divider borderColor="secondary.900" />
                    <Flex justify="space-between" w="full" align="center">
                      <Text fontSize="sm" fontWeight="medium" color="primary.900">Artists:</Text>
                      <Text fontSize="sm" color="dark.900" fontWeight="bold">
                        {artists.length} managed
                      </Text>
                    </Flex>
                    <Flex justify="space-between" w="full" align="center">
                      <Text fontSize="sm" fontWeight="medium" color="primary.900">Industry:</Text>
                      <Text fontSize="sm" color="dark.900" fontWeight="semibold" textAlign="right" maxW="60%">
                        {merchantData?.industry || 'Not specified'}
                      </Text>
                    </Flex>
                  </VStack>
                </VStack>
              </CardBody>
            </Card>
          </GridItem>
        </Grid>
      </VStack>
    </Container>
  );
};

export default MerchantDashboard;